import styled, { css } from 'styled-components'
import palette from '../../../theme/palette'
import colorScheme from '../../../theme/colorScheme'
import { Icon } from '../../atoms/Icon/Icon.styles'
import { Paragraph } from '../../atoms/Typography/Typography.styles'
import { fadeIn, fadeOut } from '../../../theme/animations'

type StepStatus = 'completed' | 'active' | 'pending'

type StepperStepStyleProps = {
  $status: StepStatus
  colorScheme?: string
}

export const StepperWrapper = styled.div<{ colorScheme?: string }>`
  width: 100%;
`

export const StepperListWrapper = styled.ol`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.rem(16)};
  list-style: none;
  margin: 0;
  padding: 0;
`

export const StepperStepWrapper = styled.li<StepperStepStyleProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.rem(16)};
`

export const StepperProgressBar = styled.div<StepperStepStyleProps>`
  height: ${({ theme }) => theme.rem(4)};
  border-radius: ${({ theme }) => theme.rem(2)};
  transition: background-color 0.3s ease;

  ${({ $status, theme, colorScheme }) =>
    $status === 'completed' &&
    css`
      background-color: ${theme.colorScheme[`${colorScheme}`].stepper.progressBar.completed};
    `}

  ${({ $status, theme, colorScheme }) =>
    $status === 'active' &&
    css`
      background-color: ${theme.colorScheme[`${colorScheme}`].stepper.progressBar.active};
    `}

  ${({ $status, theme, colorScheme }) =>
    $status === 'pending' &&
    css`
      background-color: ${theme.colorScheme[`${colorScheme}`].stepper.progressBar.pending};
    `}
`

export const StepperStepLabel = styled.div<StepperStepStyleProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.rem(6)};
  font-size: ${({ theme }) => theme.rem(14)};
  line-height: ${({ theme }) => theme.rem(20)};

  ${Paragraph} {
    color: ${({ theme, colorScheme }) => theme.colorScheme[`${colorScheme}`].stepper.stepLabel};
  }
`

export const StepperIcon = styled.div<{ colorScheme?: string }>`
  width: ${({ theme }) => theme.rem(24)};
  height: ${({ theme }) => theme.rem(24)};
  background-color: ${({ theme, colorScheme }) => theme.colorScheme[`${colorScheme}`].stepper.progressBar.completed};
  border-radius: ${({ theme }) => theme.rem(4)};
  display: flex;
  align-items: center;
  justify-content: center;

  ${Icon} {
    color: ${({ theme, colorScheme }) => theme.colorScheme[`${colorScheme}`].stepper.stepIcon};

    svg {
      width: ${({ theme }) => theme.rem(16)};
      height: ${({ theme }) => theme.rem(16)};
    }
  }
`
type StepperContentWrapperProps = {
  $animationPhase?: 'exiting' | 'entering'
}

export const StepperContentWrapper = styled.div<StepperContentWrapperProps>`
  width: 100%;
  padding: ${({ theme }) => theme.rem(50, 0)};

  ${({ $animationPhase }) =>
    $animationPhase === 'exiting' &&
    css`
      animation: ${fadeOut} 0.25s ease-in forwards;
    `}

  ${({ $animationPhase }) =>
    $animationPhase === 'entering' &&
    css`
      animation: ${fadeIn} 0.25s ease-out forwards;
    `}
`

export const StepperControlsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.rem(16)};
  padding-top: ${({ theme }) => theme.rem(16)};
`

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`
